/**
 * SIMR login role-selection session flag.
 *
 * One login identity = one primary (parent) account + its alias roles.
 * The flag is keyed by PRIMARY account id — not by alias id — so:
 * - Switching aliases within the same login does NOT re-show the login popup
 * - Logging in as a different person (different primary) DOES show the popup
 */

export function resolveSimrPrimaryUserId(
  currentUser: any,
  linkedAccounts: any[] = []
): string | null {
  const primaryFromList = (linkedAccounts || []).find(
    (acc) => acc?.is_parent === true || acc?.is_parent === 1 || acc?.is_parent === 'true'
  );
  if (primaryFromList?.id) {
    return primaryFromList.id;
  }

  const isPrimary =
    currentUser?.is_parent === true ||
    currentUser?.is_parent === 1 ||
    currentUser?.is_parent === 'true';
  if (isPrimary && currentUser?.id) {
    return currentUser.id;
  }

  const parentId = currentUser?.parent_id;
  if (parentId) {
    return typeof parentId === 'object' ? parentId.id : parentId;
  }

  return currentUser?.id ?? null;
}

export function simrRoleSelectionKey(primaryUserId: string): string {
  return `role_selection_handled_${primaryUserId}`;
}

export function isSimrRoleSelectionHandled(
  primaryUserId: string | undefined | null
): boolean {
  if (!primaryUserId) {
    return false;
  }
  return sessionStorage.getItem(simrRoleSelectionKey(primaryUserId)) === 'true';
}

export function markSimrRoleSelectionHandled(
  primaryUserId: string | undefined | null
): void {
  if (!primaryUserId) {
    return;
  }
  sessionStorage.setItem(simrRoleSelectionKey(primaryUserId), 'true');
}

/** Tracks which primary account this browser tab session belongs to. */
export function getSimrSessionPrimaryUserId(): string | null {
  return sessionStorage.getItem('simr_session_primary_user_id');
}

export function setSimrSessionPrimaryUserId(primaryUserId: string | null): void {
  if (!primaryUserId) {
    sessionStorage.removeItem('simr_session_primary_user_id');
    return;
  }
  sessionStorage.setItem('simr_session_primary_user_id', primaryUserId);
}

/**
 * When a different person logs in (new primary id), drop the old global flag
 * from earlier builds and allow role selection for the new login identity.
 */
export function onSimrLoginPrimaryUser(primaryUserId: string | null): void {
  sessionStorage.removeItem('role_selection_handled');

  if (!primaryUserId) {
    return;
  }

  const previousPrimary = getSimrSessionPrimaryUserId();
  if (previousPrimary && previousPrimary !== primaryUserId) {
    sessionStorage.removeItem(simrRoleSelectionKey(previousPrimary));
  }
  setSimrSessionPrimaryUserId(primaryUserId);
}

/** Deep link to restore after SIMR role selection (e.g. email Validate → campaign-projection). */
export const SIMR_POST_ROLE_REDIRECT_KEY = 'simr_post_role_redirect';

/** Paths that should not replace a saved deep link (e.g. email Validate → campaign-projection). */
const SIMR_GENERIC_REDIRECT_PATHS = new Set(['/', '/products', '/home', '/login']);

function isSimrGenericRedirectPath(path: string): boolean {
  const base = path.split('?')[0];
  return SIMR_GENERIC_REDIRECT_PATHS.has(base);
}

export function captureSimrPostRoleRedirect(pathname?: string, search?: string): void {
  const path =
    pathname != null && search === undefined && pathname.includes('?')
      ? pathname
      : `${pathname ?? window.location.pathname}${search ?? window.location.search}`;
  if (!path || isSimrGenericRedirectPath(path)) {
    return;
  }

  const existing = sessionStorage.getItem(SIMR_POST_ROLE_REDIRECT_KEY);
  if (existing && !isSimrGenericRedirectPath(existing)) {
    return;
  }

  sessionStorage.setItem(SIMR_POST_ROLE_REDIRECT_KEY, path);
}

export function getSimrPostRoleRedirect(): string | null {
  return sessionStorage.getItem(SIMR_POST_ROLE_REDIRECT_KEY);
}

export function clearSimrPostRoleRedirect(): void {
  sessionStorage.removeItem(SIMR_POST_ROLE_REDIRECT_KEY);
}

export function consumeSimrPostRoleRedirect(fallback = '/products'): string {
  const path = sessionStorage.getItem(SIMR_POST_ROLE_REDIRECT_KEY);
  if (path) {
    sessionStorage.removeItem(SIMR_POST_ROLE_REDIRECT_KEY);
    return path;
  }
  return fallback;
}
